class FrameBuilder extends HTMLElement {
  constructor() {
    super();
    console.log('✓ FrameBuilder initialized');
    this.product = null;
    this.selectedOptions = {};
    this.selectedVariant = null;
    this.zoomJustClosed = false;
    this.keepOpenSteps = parseInt(this.dataset.keepOpenSteps, 10) || 1;
    // Preview Carousel (only rendered when the section has Hero Images). Until
    // the shopper's first selection it shows only the Hero Images; the Live
    // Preview slide stays hidden.
    this.previewCarousel = this.querySelector('[js-preview-carousel]');
    this.heroActive = !!this.previewCarousel;
    this.init();
  }

  init() {
    // Parse product data from the JSON script tag
    this.parseProduct();

    // Set up mobile accordion behavior
    this.setupMobileAccordion();

    // Set up variant option selectors (Steps 1-3)
    this.setupVariantOptions();

    // Set up additional option steps (Steps 4-9)
    this.setupAdditionalSteps();

    // Set up info modals
    this.setupInfoModals();

    // Set up add to cart
    this.setupAddToCart();

    // Set up mobile sticky bar
    this.setupMobileStickyBar();

    // Show first step
    this.showStep(1);
  }

  setupMobileAccordion() {
    console.log('→ Setting up accordion');

    // Get all step titles
    const steps = this.querySelectorAll('.frame-step');

    steps.forEach((step, index) => {
      const title = step.querySelector('.step-title');
      if (!title) return;

      // Make title clickable
      title.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleAccordion(step);
      });
    });

    // Open the first N steps by default (N = keep-open-steps setting)
    steps.forEach((step, index) => {
      if (index < this.keepOpenSteps) {
        step.classList.add('is-open');
      }
    });

    console.log('✓ Accordion set up complete');
  }

  toggleAccordion(step) {
    const isOpen = step.classList.contains('is-open');

    // Close other open steps, except the ones configured to always stay open
    this.querySelectorAll('.frame-step.is-open').forEach(openStep => {
      const openStepNumber = parseInt(openStep.getAttribute('js-step'), 10);
      if (openStep !== step && openStepNumber > this.keepOpenSteps) {
        openStep.classList.remove('is-open');
      }
    });

    // Toggle current step
    if (isOpen) {
      step.classList.remove('is-open');
    } else {
      step.classList.add('is-open');
    }
  }

  markStepCompleted(stepNumber) {
    const step = this.querySelector(`[js-step="${stepNumber}"]`);
    if (step) {
      step.classList.add('is-completed');
    }
  }

  openNextStep(currentStepNumber) {
    // Mark current step as completed
    this.markStepCompleted(currentStepNumber);

    // Find and open next step
    const nextStep = this.querySelector(`[js-step="${currentStepNumber + 1}"]`);
    if (nextStep) {
      // Close current step
      const currentStep = this.querySelector(`[js-step="${currentStepNumber}"]`);
      if (currentStep) {
        currentStep.classList.remove('is-open');
      }

      // Open next step after a brief delay
      setTimeout(() => {
        nextStep.classList.add('is-open');
      }, 200);
    }
  }

  parseProduct() {
    const dataScript = document.getElementById('frame-builder-data');
    if (dataScript) {
      try {
        this.product = JSON.parse(dataScript.textContent);
        console.log('✓ Parsed product:', this.product.title);
        console.log('✓ Variants:', this.product.variants?.length || 0);
        console.log('✓ Options:', this.product.options || []);
      } catch (e) {
        console.error('✗ Failed to parse product data:', e);
      }
    } else {
      console.error('✗ No JSON script tag found with id="frame-builder-data"');
    }

    // Parse variant metafields data
    const variantDataScript = document.getElementById('frame-builder-variant-data');
    if (variantDataScript) {
      try {
        this.variantMetafields = JSON.parse(variantDataScript.textContent);
        console.log('✓ Parsed variant metafields:', this.variantMetafields.length);
      } catch (e) {
        console.error('✗ Failed to parse variant metafields:', e);
      }
    } else {
      console.error('✗ No JSON script tag found with id="frame-builder-variant-data"');
    }

    // Parse metaobjects data (add-on variant IDs)
    const metaobjectsDataScript = document.getElementById('frame-builder-metaobjects-data');
    if (metaobjectsDataScript) {
      try {
        this.metaobjectsData = JSON.parse(metaobjectsDataScript.textContent);
        console.log('✓ Parsed metaobjects data:', this.metaobjectsData);
        this.mosaicPrices = this.parseMosaicPrices(this.metaobjectsData.mosaic_prices);
      } catch (e) {
        console.error('✗ Failed to parse metaobjects data:', e);
      }
    } else {
      console.error('✗ No JSON script tag found with id="frame-builder-metaobjects-data"');
    }
  }

  // Parses the "Size:Price" lines from the mosaic_prices section setting.
  parseMosaicPrices(text) {
    const prices = {};
    (text || '').split('\n').forEach((line) => {
      const separator = line.lastIndexOf(':');
      if (separator === -1) return;
      const size = line.slice(0, separator).trim();
      const price = parseFloat(line.slice(separator + 1));
      if (size && price > 0) prices[size] = price;
    });
    return prices;
  }

  // Mosaic Surcharge for the selected size. Disabled until both the Mosaic add-on
  // variant and the price list are configured; price is null for unlisted sizes.
  getMosaicSurcharge() {
    if (!this.metaobjectsData?.mosaic_variant || !Object.keys(this.mosaicPrices || {}).length) {
      return { enabled: false, price: null };
    }
    const sizeIndex = (this.product?.options || []).findIndex((name) => String(name).includes('Size'));
    const size = sizeIndex === -1 ? null : this.selectedVariant?.[`option${sizeIndex + 1}`];
    return { enabled: true, price: this.mosaicPrices[size] ?? null };
  }

  setupVariantOptions() {
    console.log('→ Setting up variant options');

    // Get all option selectors (Steps 1-3)
    const optionSelectors = this.querySelectorAll('[data-option-index]');
    this.optionCount = optionSelectors.length;

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);
      const optionName = selector.dataset.optionName;

      // Get all radio inputs for this option
      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        // Initialize selectedOptions with first checked values
        if (radio.checked) {
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
        }

        // Clicking a radio that's already selected doesn't fire `change`
        // (its checked state never flips), so it would otherwise do nothing.
        // Advance on every click instead — friendlier UX, and harmless to
        // also re-run when `change` fires right after for a real switch.
        radio.addEventListener('click', () => {
          if (radio.checked) {
            this.handleOptionChange(optionIndex, radio.value);
          }
        });

        radio.addEventListener('change', () => {
          if (radio.checked) {
            this.handleOptionChange(optionIndex, radio.value);
          }
        });
      });
    });

    // Filter options in Steps 2 and 3 based on Step 1's initial selection
    this.updateOptionAvailability();

    // Auto-select first available option in Steps 2 and 3 if current selection is unavailable
    this.autoSelectFirstAvailableOption(1);
    this.autoSelectFirstAvailableOption(2);

    // Find initial variant
    this.updateSelectedVariant();

    console.log('✓ Variant options set up complete');
  }

  handleOptionChange(optionIndex, value) {
    // First real selection — reveal the Live Preview as slide 1
    this.revealLivePreview();

    // Update selected option
    this.selectedOptions[`option${optionIndex + 1}`] = value;

    // Update availability of dependent options (Steps 2 and 3)
    this.updateOptionAvailability();

    // Auto-select first available option in next steps if current selection is unavailable
    // For example, if user selects "Cathedral" in Step 1, and current Step 2 selection
    // (like "6x8") doesn't exist for Cathedral, auto-select first available size
    if (optionIndex === 0) {
      // Changed Step 1, check Step 2 and 3
      this.autoSelectFirstAvailableOption(1); // Check Step 2
      this.autoSelectFirstAvailableOption(2); // Check Step 3
    } else if (optionIndex === 1) {
      // Changed Step 2, check Step 3
      this.autoSelectFirstAvailableOption(2); // Check Step 3
    }

    // Find matching variant (after potential auto-selection)
    this.updateSelectedVariant();

    // Shape/Size/Finish changed the Live Preview — bring it into view
    this.previewCarousel?.select(0);

    // Current step number is optionIndex + 1 (since optionIndex is 0-based)
    const currentStepNumber = optionIndex + 1;

    // Show next step
    const nextStep = optionIndex + 2; // +1 for next option, +1 because optionIndex is 0-based

    // If we have selected all variant options, show step 4 (first additional option)
    const totalOptions = Object.keys(this.selectedOptions).length;
    if (totalOptions >= 3 || optionIndex >= 2) {
      this.showStep(4);
    } else {
      this.showStep(nextStep);
    }

    // Update summary
    this.updateSummary();
  }

  // First variant matching whatever the shopper has picked so far. With
  // nothing picked this is the first variant, so callers must only use it
  // for previewing an image, never for pricing.
  findVariantMatchingSelections() {
    if (!this.product || !this.product.variants) return null;

    return this.product.variants.find(variant => {
      return Object.keys(this.selectedOptions).every(key => {
        return variant[key] === this.selectedOptions[key];
      });
    }) || null;
  }

  hasCompleteSelection() {
    return Object.keys(this.selectedOptions).length >= (this.optionCount || 0);
  }

  // The variant whose image should be in the preview: the resolved variant
  // once Shape/Size/Finish are all chosen, otherwise the closest partial match.
  getPreviewVariant() {
    return this.selectedVariant || this.findVariantMatchingSelections();
  }

  updateSelectedVariant() {
    if (!this.product || !this.product.variants) {
      console.error('✗ No product or variants available');
      return;
    }

    // Nothing is pre-selected, so a variant (and its price) only resolves once
    // the shopper has chosen every option. Until then just preview an image.
    if (!this.hasCompleteSelection()) {
      this.selectedVariant = null;
      this.updatePreviewImage(this.getPreviewVariant());
      this.updateSummary();
      return;
    }

    // Find variant that matches all selected options
    const matchingVariant = this.findVariantMatchingSelections();

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;

      // Update preview image
      this.updatePreviewImage(matchingVariant);

      // Update dynamic options based on variant metafields
      this.updateDynamicOptions(matchingVariant);

      // Update pricing
      this.updateSummary();

      // Update mobile sticky bar price
      this.updateStickyBarPrice();
    } else {
      this.selectedVariant = null;
    }
  }

  getVariantMetafields(variantId) {
    if (!this.variantMetafields || !variantId) {
      return null;
    }
    return this.variantMetafields[variantId] || null;
  }

  updateDynamicOptions(variant) {
    if (!variant) return;

    const metafields = this.getVariantMetafields(variant.id);
    if (!metafields) {
      console.warn('⚠ No metafields found for variant:', variant.id);
      return;
    }

    console.log('→ Updating dynamic options for variant:', variant.id, metafields);

    // Update backing options
    this.populateBackingOptions(metafields.backing_options, metafields.linen_matte_price);

    // Update glass type options
    this.populateGlassType(metafields.glass_type, metafields.art_glass_price);

    // Update design styles
    this.populateDesignStyles(metafields.design_style);
  }

  populateBackingOptions(options, linenMattePrice) {
    const container = this.querySelector('[js-backing-options]');
    if (!container || !options) return;

    // Store current selection
    const currentSelection = this.querySelector('input[name="backing_options"]:checked')?.value;

    container.innerHTML = '';

    options.forEach((option, index) => {
      const label = document.createElement('label');
      label.className = 'option-item';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'backing_options';
      input.value = option;

      // Check first option by default, or keep current selection if it exists
      if ((currentSelection && option === currentSelection) || (!currentSelection && index === 0)) {
        input.checked = true;
      }

      const span = document.createElement('span');
      span.className = 'option-label';

      // Add price for linen matte options if price exists
      if (linenMattePrice && option.toLowerCase().includes('linen')) {
        input.dataset.price = linenMattePrice;
        span.innerHTML = `${option} <span class="price-addon">+$${parseFloat(linenMattePrice).toFixed(2)}</span>`;
      } else {
        span.textContent = option;
      }

      label.appendChild(input);
      label.appendChild(span);
      container.appendChild(label);

      // Add change + click listeners (click also covers re-selecting the
      // already-checked option, which never fires `change`)
      const advance = () => {
        console.log('✓ backing_options selected:', input.value);
        this.updateSummary();
        this.showStep(6);
      };
      input.addEventListener('change', advance);
      input.addEventListener('click', () => {
        if (input.checked) advance();
      });
    });

    console.log('✓ Populated backing options:', options);
  }

  populateGlassType(options, artGlassPrice) {
    const container = this.querySelector('[js-glass-type]');
    if (!container || !options) return;

    // Store current selection
    const currentSelection = this.querySelector('input[name="glass_type"]:checked')?.value;

    container.innerHTML = '';

    options.forEach((option, index) => {
      const label = document.createElement('label');
      label.className = 'option-item';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'glass_type';
      input.value = option;

      // Check first option by default, or keep current selection if it exists
      if ((currentSelection && option === currentSelection) || (!currentSelection && index === 0)) {
        input.checked = true;
      }

      const span = document.createElement('span');
      span.className = 'option-label';

      // Add price for art glass if price exists
      if (artGlassPrice && option.toLowerCase().includes('art')) {
        input.dataset.price = artGlassPrice;
        span.innerHTML = `${option} <span class="price-addon">+$${parseFloat(artGlassPrice).toFixed(2)}</span>`;
      } else {
        span.textContent = option;
      }

      label.appendChild(input);
      label.appendChild(span);
      container.appendChild(label);

      // Add change + click listeners (click also covers re-selecting the
      // already-checked option, which never fires `change`)
      const advance = () => {
        console.log('✓ glass_type selected:', input.value);
        this.updateSummary();
        this.showStep(7);
      };
      input.addEventListener('change', advance);
      input.addEventListener('click', () => {
        if (input.checked) advance();
      });
    });

    console.log('✓ Populated glass type:', options);
  }

  populateDesignStyles(designStyles) {
    const container = this.querySelector('[js-design-styles]');
    if (!container) return;

    const currentShape = (this.selectedOptions && this.selectedOptions['option1']) || '';
    container.classList.add('option-selector--design-styles');

    // Mosaic is offered only for sizes that have a Mosaic Surcharge
    const mosaic = this.getMosaicSurcharge();
    const isMosaic = (option) => option.toLowerCase() === 'mosaic';
    const options = mosaic.enabled && mosaic.price === null
      ? (designStyles || []).filter((option) => !isMosaic(option))
      : designStyles;

    if (!options || !options.length) {
      // Clear stale tiles so a previous shape's tiles don't linger when this variant has no design styles
      container.innerHTML = '';
      return;
    }

    let imageMap = null;
    try {
      imageMap = JSON.parse(container.dataset.designStyleImages || 'null');
    } catch (e) {
      console.warn('⚠ Failed to parse design style images map', e);
    }

    const shapeKey = currentShape.toLowerCase();
    const placeholderSvg = (imageMap && imageMap.placeholder) || '';

    // Store current selection; fall back to the first style if it's no longer offered
    const currentSelection = this.querySelector('input[name="design_styles"]:checked')?.value;
    const keepSelection = options.includes(currentSelection);

    container.innerHTML = '';

    options.forEach((option, index) => {
      const label = document.createElement('label');
      label.className = 'option-item option-item--image';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'design_styles';
      input.value = option;

      // Keep the current selection if it's still offered, otherwise check the first option
      if ((keepSelection && option === currentSelection) || (!keepSelection && index === 0)) {
        input.checked = true;
      }

      const styleKey = option.toLowerCase();
      const imageUrl = imageMap && imageMap[shapeKey] && imageMap[shapeKey][styleKey];

      const imageWrap = document.createElement('span');
      imageWrap.className = 'option-image';

      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = currentShape ? `${option} design style for ${currentShape} frame` : `${option} design style`;
        img.loading = 'lazy';
        imageWrap.appendChild(img);
      } else {
        imageWrap.innerHTML = placeholderSvg;
      }

      const span = document.createElement('span');
      span.className = 'option-label';
      span.textContent = option;

      if (mosaic.price !== null && isMosaic(option)) {
        input.dataset.price = mosaic.price;
        const priceLabel = document.createElement('span');
        priceLabel.className = 'price-addon';
        priceLabel.textContent = `+$${mosaic.price.toFixed(2)}`;
        span.appendChild(priceLabel);
      }

      label.appendChild(input);
      label.appendChild(imageWrap);
      label.appendChild(span);
      container.appendChild(label);

      // Add change + click listeners (click also covers re-selecting the
      // already-checked option, which never fires `change`)
      const advance = () => {
        console.log('✓ design_styles selected:', input.value);
        this.updateSummary();
        this.showStep(8); // Date step
      };
      input.addEventListener('change', advance);
      input.addEventListener('click', () => {
        if (input.checked) advance();
      });
    });

    console.log('✓ Populated design styles:', options);
  }

  updateOptionAvailability() {
    // Get all option selectors
    const optionSelectors = this.querySelectorAll('[data-option-index]');

    optionSelectors.forEach((selector) => {
      const optionIndex = parseInt(selector.dataset.optionIndex);

      // Skip availability checking for Step 1 (option index 0)
      // Step 1 should always show all options as available
      if (optionIndex === 0) {
        return;
      }

      const radios = selector.querySelectorAll('input[type="radio"]');

      radios.forEach(radio => {
        // Build test options with only the options UP TO and INCLUDING current option
        // For Step 2, only check option1 + option2
        // For Step 3, only check option1 + option2 + option3
        const testOptions = {};

        // Add all options before the current one
        for (let i = 1; i <= optionIndex; i++) {
          if (this.selectedOptions[`option${i}`]) {
            testOptions[`option${i}`] = this.selectedOptions[`option${i}`];
          }
        }

        // Add the current option being tested
        testOptions[`option${optionIndex + 1}`] = radio.value;

        // Check if any variant exists with these options
        // Only check the keys we've set (don't check future options)
        const matchingVariants = this.product.variants.filter(variant => {
          return Object.keys(testOptions).every(key => {
            return variant[key] === testOptions[key];
          });
        });

        // Check if variant exists at all (not just available)
        const variantExists = matchingVariants.length > 0;
        const hasAvailableVariant = matchingVariants.some(v => v.available);

        const label = radio.closest('.option-item');
        if (label) {
          if (!variantExists) {
            // Variant doesn't exist for this combination - hide it completely
            label.style.display = 'none';
            radio.disabled = true;
          } else if (!hasAvailableVariant) {
            // Variant exists but is sold out - show as unavailable
            label.style.display = '';
            label.classList.add('unavailable');
            radio.disabled = true;

            // Add "Sold Out" label if not already present
            const optionLabel = label.querySelector('.option-label');
            if (optionLabel && !optionLabel.querySelector('.sold-out-label')) {
              const soldOutLabel = document.createElement('span');
              soldOutLabel.className = 'sold-out-label';
              soldOutLabel.textContent = ' (Sold Out)';
              optionLabel.appendChild(soldOutLabel);
            }
          } else {
            // Variant exists and is available - show normally
            label.style.display = '';
            label.classList.remove('unavailable');
            radio.disabled = false;

            // Remove "Sold Out" label if present
            const optionLabel = label.querySelector('.option-label');
            const soldOutLabel = optionLabel?.querySelector('.sold-out-label');
            if (soldOutLabel) {
              soldOutLabel.remove();
            }
          }
        }
      });
    });
  }

  autoSelectFirstAvailableOption(optionIndex) {
    // Find the selector for this option
    const selector = this.querySelector(`[data-option-index="${optionIndex}"]`);
    if (!selector) return;

    // Get all radios in this selector
    const radios = selector.querySelectorAll('input[type="radio"]');

    // Check if currently selected option is available (not hidden and not unavailable)
    const currentlySelected = selector.querySelector('input[type="radio"]:checked');
    const currentLabel = currentlySelected?.closest('.option-item');
    const isCurrentHidden = currentLabel?.style.display === 'none';
    const isCurrentUnavailable = currentLabel?.classList.contains('unavailable');
    const isCurrentAvailable = !isCurrentHidden && !isCurrentUnavailable;

    // If current option is unavailable or hidden, select first available option
    if (currentlySelected && !isCurrentAvailable) {
      // Find first available radio (not hidden and not unavailable)
      for (const radio of radios) {
        const label = radio.closest('.option-item');
        const isHidden = label?.style.display === 'none';
        const isUnavailable = label?.classList.contains('unavailable');
        if (!isHidden && !isUnavailable) {
          radio.checked = true;

          // Update selectedOptions directly (don't trigger change to avoid recursion)
          this.selectedOptions[`option${optionIndex + 1}`] = radio.value;
          return;
        }
      }
    }
  }

  revealLivePreview() {
    if (!this.heroActive) return;
    this.heroActive = false;

    // Un-hide the Live Preview slide and its dot (scroll-carousel keeps the navigation in sync)
    const carousel = this.previewCarousel;
    carousel.filter([]);
    this.querySelector('[js-preview-carousel-dots]')?.removeAttribute('hidden');

    // The carousel only re-checks scrollability on resize; a new slide doesn't resize it,
    // so update the flag the prev/next buttons watch (matters when there was a single Hero Image)
    carousel.classList.toggle('is-scrollable', carousel.scrollWidth > carousel.clientWidth);

    // Render the shopper's selection so far into the Live Preview slide
    this.updatePreviewImage(this.getPreviewVariant());
  }

  updatePreviewImage(variant) {
    // Keep the Live Preview empty until the shopper makes their first selection
    if (this.heroActive) return;

    const imageElement = this.querySelector('[js-frame-image]');
    const imageContainer = this.querySelector('[js-image]');

    let imageUrl = null;

    if (variant) {
      // Try multiple ways to get variant image (Shopify returns images in different formats)
      if (variant.featured_image?.src) {
        // featured_image as object with src property
        imageUrl = variant.featured_image.src;
      } else if (typeof variant.featured_image === 'string' && variant.featured_image) {
        // featured_image as string URL
        imageUrl = variant.featured_image;
      } else if (variant.image?.src) {
        // image as object with src property
        imageUrl = variant.image.src;
      } else if (typeof variant.image === 'string' && variant.image) {
        // image as string URL
        imageUrl = variant.image;
      } else if (variant.image_id && this.product.images) {
        // Find image in product.images array by image_id
        const matchingImage = this.product.images.find(img => img.id === variant.image_id);
        if (matchingImage) {
          imageUrl = matchingImage.src || matchingImage;
        }
      }
    }

    // Fall back to product featured image if no variant image
    if (!imageUrl && this.product.featured_image) {
      if (typeof this.product.featured_image === 'string') {
        imageUrl = this.product.featured_image;
      } else if (this.product.featured_image.src) {
        imageUrl = this.product.featured_image.src;
      }
    }

    // Fall back to first product image if available
    if (!imageUrl && this.product.images && this.product.images.length > 0) {
      const firstImage = this.product.images[0];
      imageUrl = firstImage.src || firstImage;
    }

    if (imageElement && imageUrl) {
      // Ensure the URL has a protocol (Shopify returns protocol-relative URLs)
      const fullImageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;

      // Update image source
      imageElement.src = fullImageUrl;
      imageElement.style.display = 'block';
      imageElement.style.opacity = '1';

      // Add fade-in animation
      if (imageContainer) {
        imageContainer.classList.add('image-loaded');
      }
    } else {
      if (imageElement) {
        imageElement.style.display = 'none';
      }
    }
  }

  showStep(stepNumber) {

    // Show all steps up to and including the selected step
    for (let i = 1; i <= stepNumber; i++) {
      const step = this.querySelector(`[js-step="${i}"]`);
      if (step) {
        step.style.display = 'block';
        if (i === stepNumber) {
          step.classList.add('active');
        }
      }
    }

    this.openAccordionStep(stepNumber);
  }

  openAccordionStep(stepNumber) {
    const newStep = this.querySelector(`[js-step="${stepNumber}"]`);
    if (!newStep) return;

    // Auto-collapse previously open steps, except the ones configured to always stay open
    this.querySelectorAll('.frame-step.is-open').forEach(openStep => {
      const openStepNumber = parseInt(openStep.getAttribute('js-step'), 10);
      if (openStep !== newStep && openStepNumber > this.keepOpenSteps) {
        openStep.classList.remove('is-open');
      }
    });

    newStep.classList.add('is-open');
  }

  setupAdditionalSteps() {
    console.log('→ Setting up additional option steps');

    // Set up radio button handlers for static steps (4, 9)
    // Steps 5, 6, 7 are now dynamically populated and their listeners are added when created
    const radioSteps = [
      { name: 'color_correction', currentStep: 4, nextStep: 5 },
      { name: 'shipping_kit', currentStep: 9, nextStep: 10 }
    ];

    radioSteps.forEach(({ name, currentStep, nextStep }) => {
      const radios = this.querySelectorAll(`input[name="${name}"]`);
      const advance = (radio) => {
        console.log(`✓ ${name} selected:`, radio.value);
        this.updateSummary();
        this.showStep(nextStep);
      };
      radios.forEach(radio => {
        radio.addEventListener('change', () => advance(radio));
        // Re-clicking the already-selected option never fires `change` —
        // advance on click too so it still moves to the next step.
        radio.addEventListener('click', () => {
          if (radio.checked) advance(radio);
        });
      });
    });

    // Set up date picker handler
    const dateInput = this.querySelector('[js-shipping-date]');
    if (dateInput) {
      dateInput.addEventListener('change', () => {
        console.log('✓ Shipping date selected:', dateInput.value);
        this.updateSummary();
        this.showStep(9); // Shipping kit step
      });
    }

    console.log('✓ Additional steps set up complete');
  }

  getSelections() {
    // Helper method to get all user selections
    const selections = {
      variant: this.selectedVariant,
      colorCorrection: this.querySelector('input[name="color_correction"]:checked')?.value,
      backingOptions: this.querySelector('input[name="backing_options"]:checked')?.value,
      backingOptionsInput: this.querySelector('input[name="backing_options"]:checked'),
      glassType: this.querySelector('input[name="glass_type"]:checked')?.value,
      glassTypeInput: this.querySelector('input[name="glass_type"]:checked'),
      designStyles: this.querySelector('input[name="design_styles"]:checked')?.value,
      designStylesInput: this.querySelector('input[name="design_styles"]:checked'),
      shippingKit: this.querySelector('input[name="shipping_kit"]:checked')?.value,
      shippingKitInput: this.querySelector('input[name="shipping_kit"]:checked'),
      shippingDate: this.querySelector('[js-shipping-date]')?.value
    };

    // Add selected options with their labels
    Object.keys(this.selectedOptions).forEach((key, index) => {
      const optionName = this.product.options[index];
      selections[`option${index + 1}`] = this.selectedOptions[key];
      selections[`option${index + 1}_name`] = optionName;
    });

    return selections;
  }

  updateSummary() {
    const selections = this.getSelections();

    // Update variant option values in summary
    if (selections.option1_name) {
      const summaryElement = this.querySelector('[js-summary-option1]') || this.querySelector('[js-summary-shape]');
      if (summaryElement) {
        summaryElement.textContent = selections.option1 || '-';
      }
    }

    if (selections.option2_name) {
      const summaryElement = this.querySelector('[js-summary-option2]') || this.querySelector('[js-summary-size]');
      if (summaryElement) {
        summaryElement.textContent = selections.option2 || '-';
      }
    }

    if (selections.option3_name) {
      const summaryElement = this.querySelector('[js-summary-option3]') || this.querySelector('[js-summary-finish]');
      if (summaryElement) {
        summaryElement.textContent = selections.option3 || '-';
      }
    }

    // Update additional selections
    const colorElement = this.querySelector('[js-summary-color]');
    if (colorElement) colorElement.textContent = selections.colorCorrection || '-';

    const backingElement = this.querySelector('[js-summary-backing]');
    if (backingElement) backingElement.textContent = selections.backingOptions || '-';

    const glassElement = this.querySelector('[js-summary-glass]');
    if (glassElement) glassElement.textContent = selections.glassType || '-';

    const designElement = this.querySelector('[js-summary-design]');
    if (designElement) designElement.textContent = selections.designStyles || '-';

    const shippingElement = this.querySelector('[js-summary-shipping]');
    if (shippingElement) {
      const shippingLabels = {
        'full_kit': 'Flower Shipping Box + Prepaid Label',
        'diy_kit': 'DIY Kit (No Label)',
        'label_only': 'Shipping Label Only',
        'none': 'No Shipping Kit',
        'drop_off': 'Local Drop-Off'
      };
      shippingElement.textContent = shippingLabels[selections.shippingKit] || selections.shippingKit || '-';
    }

    const dateElement = this.querySelector('[js-summary-date]');
    if (dateElement) dateElement.textContent = selections.shippingDate || '-';

    // Calculate prices
    let basePrice = 0;
    const extras = [];

    if (this.selectedVariant) {
      basePrice = this.selectedVariant.price / 100; // Shopify prices are in cents

      // Check for backing option extras
      const backingInput = selections.backingOptionsInput;
      if (backingInput?.dataset.price) {
        const extraPrice = parseFloat(backingInput.dataset.price);
        extras.push({
          label: selections.backingOptions,
          price: extraPrice
        });
      }

      // Check for glass type extras
      const glassInput = selections.glassTypeInput;
      if (glassInput?.dataset.price) {
        const extraPrice = parseFloat(glassInput.dataset.price);
        extras.push({
          label: selections.glassType,
          price: extraPrice
        });
      }

      // Check for Mosaic Surcharge
      const designInput = selections.designStylesInput;
      if (designInput?.dataset.price) {
        extras.push({
          label: selections.designStyles,
          price: parseFloat(designInput.dataset.price)
        });
      }

      // Check for shipping kit extras
      const shippingInput = selections.shippingKitInput;
      if (shippingInput?.dataset.price && parseFloat(shippingInput.dataset.price) > 0) {
        const extraPrice = parseFloat(shippingInput.dataset.price);
        const shippingLabels = {
          'full_kit': 'Shipping Box + Prepaid Label',
          'label_only': 'Shipping Label Only'
        };
        extras.push({
          label: shippingLabels[shippingInput.value] || 'Shipping Kit',
          price: extraPrice
        });
      }
    }

    // Update price display
    const basePriceElement = this.querySelector('[js-base-price]');
    if (basePriceElement) {
      basePriceElement.textContent = this.formatPrice(basePrice);
    }

    // Update extras
    const extrasContainer = this.querySelector('[js-price-extras]');
    if (extrasContainer) {
      extrasContainer.innerHTML = '';
      extras.forEach(extra => {
        const extraDiv = document.createElement('div');
        extraDiv.className = 'price-extra-item';
        extraDiv.innerHTML = `
          <span class="price-extra-label">${extra.label}:</span>
          <span class="price-extra-value">+${this.formatPrice(extra.price)}</span>
        `;
        extrasContainer.appendChild(extraDiv);
      });
    }

    // Calculate and display total
    const extrasTotal = extras.reduce((sum, extra) => sum + extra.price, 0);
    const total = basePrice + extrasTotal;
    const totalPriceElement = this.querySelector('[js-total-price]');
    if (totalPriceElement) {
      totalPriceElement.textContent = this.formatPrice(total);
    }

    // Show summary section
    const summarySection = this.querySelector('[js-summary]');
    if (summarySection && this.selectedVariant) {
      summarySection.style.display = 'block';
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  setupAddToCart() {
    console.log('→ Setting up add to cart');

    const addToCartBtn = this.querySelector('[js-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => {
        this.handleAddToCart();
      });
    }

    console.log('✓ Add to cart set up complete');
  }

  setupMobileStickyBar() {
    console.log('→ Setting up mobile sticky bar');

    const stickyBar = this.querySelector('[js-mobile-sticky-bar]');
    const reviewBtn = this.querySelector('[js-review-selections]');
    const summarySection = this.querySelector('[js-summary]');

    if (!stickyBar || !reviewBtn || !summarySection) {
      console.log('⚠ Mobile sticky bar elements not found');
      return;
    }

    // Handle "Review Selections" button click
    reviewBtn.addEventListener('click', () => {
      // Scroll to summary section
      summarySection.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Hide the sticky bar after scrolling
      setTimeout(() => {
        stickyBar.classList.add('is-hidden');
      }, 500);
    });

    // Set up intersection observer to show/hide sticky bar based on summary visibility
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const summaryObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Summary is visible, hide sticky bar
          stickyBar.classList.add('is-hidden');
        } else {
          // Summary is not visible, show sticky bar (only if summary is displayed)
          if (summarySection.style.display !== 'none') {
            stickyBar.classList.remove('is-hidden');
          }
        }
      });
    }, observerOptions);

    summaryObserver.observe(summarySection);

    console.log('✓ Mobile sticky bar set up complete');
  }

  updateStickyBarPrice() {
    const stickyPriceElement = this.querySelector('[js-sticky-price]');
    if (!stickyPriceElement || !this.selectedVariant) return;

    const basePrice = this.selectedVariant.price / 100;
    stickyPriceElement.textContent = this.formatPrice(basePrice);
  }

  async handleAddToCart() {
    const addToCartBtn = this.querySelector('[js-add-to-cart]');

    // Get all selections
    const selections = this.getSelections();
    console.log('→ Adding to cart with selections:', selections);

    // Validate selections
    if (!this.selectedVariant) {
      alert('Please complete all selections before adding to cart');
      return;
    }

    if (!this.selectedVariant.available) {
      alert('This variant is currently out of stock');
      return;
    }

    // Get quantity
    const quantityInput = this.querySelector('[name="quantity"]');
    const quantity = parseInt(quantityInput?.value || 1);

    // Get variant metafields for pricing
    const metafields = this.getVariantMetafields(this.selectedVariant.id);

    // Build components array for add-ons
    const components = [];

    // Add parent frame variant as first component
    const basePrice = this.selectedVariant.price / 100; // Shopify prices are in cents

    components.push({
      id: this.selectedVariant.id,
      quantity: 1,
      price: basePrice
    });

    // Add Art Glass component if selected
    if (selections.glassTypeInput?.dataset.price && this.metaobjectsData?.art_glass_variant) {
      const artGlassPrice = parseFloat(selections.glassTypeInput.dataset.price);
      components.push({
        id: this.metaobjectsData.art_glass_variant,
        quantity: 1,
        price: artGlassPrice
      });
    }

    // Add Linen Matte component if selected
    if (selections.backingOptionsInput?.dataset.price && this.metaobjectsData?.linen_matte_variant) {
      const linenMattePrice = parseFloat(selections.backingOptionsInput.dataset.price);
      components.push({
        id: this.metaobjectsData.linen_matte_variant,
        quantity: 1,
        price: linenMattePrice
      });
    }

    // Add Mosaic Surcharge component if selected
    if (selections.designStylesInput?.dataset.price && this.metaobjectsData?.mosaic_variant) {
      components.push({
        id: this.metaobjectsData.mosaic_variant,
        quantity: 1,
        price: parseFloat(selections.designStylesInput.dataset.price)
      });
    }

    // Add Shipping Kit component if selected and has a price
    if (selections.shippingKitInput?.dataset.price && parseFloat(selections.shippingKitInput.dataset.price) > 0 && this.metaobjectsData?.shipping_kit_variant) {
      const shippingKitPrice = parseFloat(selections.shippingKitInput.dataset.price);
      const shippingTypeLabels = {
        'full_kit': 'Flower Shipping Box + Prepaid Label',
        'label_only': 'Shipping Label Only'
      };
      components.push({
        id: this.metaobjectsData.shipping_kit_variant,
        quantity: 1,
        price: shippingKitPrice,
        attributes: {
          "Type": shippingTypeLabels[selections.shippingKit] || "Shipping Kit",
          "Shipping Option": shippingTypeLabels[selections.shippingKit] || selections.shippingKit
        }
      });
    }

    // Build attributes for the main product component
    const mainProductAttributes = {};

    // Add variant options as attributes
    for (let i = 1; i <= 3; i++) {
      if (selections[`option${i}_name`] && selections[`option${i}`]) {
        mainProductAttributes[selections[`option${i}_name`]] = selections[`option${i}`];
      }
    }

    // Add additional selections as attributes
    if (selections.colorCorrection) {
      mainProductAttributes['Color Correction'] = selections.colorCorrection;
    }
    if (selections.backingOptions) {
      mainProductAttributes['Backing'] = selections.backingOptions;
    }
    if (selections.glassType) {
      mainProductAttributes['Glass Type'] = selections.glassType;
    }
    if (selections.designStyles) {
      mainProductAttributes['Design Style'] = selections.designStyles;
    }
    if (selections.shippingKit) {
      const shippingLabels = {
        'full_kit': 'Flower Shipping Box + Prepaid Label ($150)',
        'diy_kit': 'DIY Kit (No Label)',
        'label_only': 'Shipping Label Only ($125)',
        'none': 'No Shipping Kit',
        'drop_off': 'Local Drop-Off'
      };
      mainProductAttributes['Shipping Kit'] = shippingLabels[selections.shippingKit] || selections.shippingKit;
    }
    if (selections.shippingDate) {
      mainProductAttributes['Bouquet Shipping Date'] = selections.shippingDate;
    }

    // Add attributes to the main product component (first component)
    if (components.length > 0) {
      components[0].attributes = mainProductAttributes;
    }

    // Build line item properties
    const properties = {};

    // Add _settings with title
    properties['_settings'] = JSON.stringify({
      title: this.metaobjectsData?.frame_product_title || this.product?.title || 'Pressed Frame Bundle'
    });

    // Add components to properties
    if (components.length > 0) {
      properties['_components'] = JSON.stringify(components);
    }

    console.log('→ Line item properties:', properties);
    console.log('→ Components:', components);

    // Build request data
    const data = {
      items: [{
        id: this.selectedVariant.id,
        quantity: quantity,
        properties: properties
      }]
    };

    console.log('→ Request data:', data);

    // Disable button
    addToCartBtn.disabled = true;
    addToCartBtn.textContent = 'Adding...';

    // Log the exact payload being sent to add.js
    const payload = JSON.stringify(data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📦 PAYLOAD BEING SENT TO /cart/add.js:');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(payload);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📋 FORMATTED PAYLOAD:');
    console.log(JSON.parse(payload));
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if (data.items[0].properties._components) {
      console.log('🔧 COMPONENTS (parsed):');
      console.log(JSON.parse(data.items[0].properties._components));
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✓ Added to cart:', result);

      // Success feedback
      addToCartBtn.textContent = 'Added to Cart!';
      setTimeout(() => {
        addToCartBtn.textContent = 'Add to Cart';
        addToCartBtn.disabled = false;
      }, 2000);

      // Trigger cart drawer to open
      this.openCartDrawer();

    } catch (error) {
      console.error('✗ Failed to add to cart:', error);
      alert('Failed to add to cart. Please try again.');
      addToCartBtn.textContent = 'Add to Cart';
      addToCartBtn.disabled = false;
    }
  }

  openCartDrawer() {
    console.log('→ Opening cart drawer');

    // Method 1: Dispatch custom event for cart update
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('theme:cart:change'));

    // Method 2: Try common theme-specific cart drawer triggers
    if (typeof window.theme !== 'undefined' && window.theme.openCartDrawer) {
      window.theme.openCartDrawer();
      console.log('✓ Called window.theme.openCartDrawer()');
    } else if (typeof window.Shopify !== 'undefined' && window.Shopify.theme && window.Shopify.theme.cart) {
      window.Shopify.theme.cart.open();
      console.log('✓ Called window.Shopify.theme.cart.open()');
    }

    // Method 3: Try to find and click cart drawer toggle with many selectors
    const selectors = [
      '[data-cart-drawer-toggle]',
      '.cart-drawer-toggle',
      '[data-action="toggle-cart"]',
      '[aria-controls*="cart"]',
      '.header__icon--cart',
      '#cart-icon-bubble',
      'a[href="/cart"]',
      '.cart-count-bubble',
      '[data-cart-icon]',
      'cart-notification-button',
      'cart-drawer-toggle',
      '.cart-link',
      '[data-open-cart]'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`✓ Found cart element: ${selector}`);
        element.click();
        console.log('✓ Clicked cart drawer toggle');
        break;
      }
    }

    // Method 4: Try to open cart drawer by setting attribute
    const cartDrawer = document.querySelector('cart-drawer, [id*="cart-drawer"], .cart-drawer');
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active', 'open');
      console.log('✓ Set cart drawer open attribute');
    }

    console.log('✓ Cart drawer triggers dispatched');
  }

  setupInfoModals() {
    console.log('→ Setting up info modals');

    // Info Links open their step's Info Modal
    const infoLinks = this.querySelectorAll('.step-info__link');
    infoLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal(link.dataset.modal);
      });
    });

    // Set up modal close handlers
    const modals = this.querySelectorAll('.info-modal');
    modals.forEach(modal => {
      // Close button
      const closeBtn = modal.querySelector('.modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.closeModal(modal.id);
        });
      }

      // Overlay click
      const overlay = modal.querySelector('.modal-overlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          // Don't close modal if zoom was just closed
          if (this.zoomJustClosed) {
            return;
          }
          this.closeModal(modal.id);
        });
      }

      // Set up zoom triggers within this modal
      const zoomTriggers = modal.querySelectorAll('.zoom-trigger');
      const zoomOverlay = modal.querySelector('.zoom-overlay');
      const zoomImage = modal.querySelector('.zoom-image');
      const zoomClose = modal.querySelector('.zoom-close');

      if (zoomOverlay && zoomImage) {
        zoomTriggers.forEach(trigger => {
          trigger.addEventListener('click', () => {
            const zoomSrc = trigger.dataset.zoomSrc;
            if (zoomSrc) {
              zoomImage.src = zoomSrc;
              zoomOverlay.style.display = 'flex';
            }
          });
        });

        // Close zoom on close button click
        if (zoomClose) {
          zoomClose.addEventListener('click', (e) => {
            e.stopPropagation();
            this.closeZoom(zoomOverlay, zoomImage);
          });
        }

        // Close zoom on overlay click (outside image)
        zoomOverlay.addEventListener('click', (e) => {
          e.stopPropagation();
          if (e.target === zoomOverlay) {
            this.closeZoom(zoomOverlay, zoomImage);
          }
        });
      }
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // First check for open zoom overlay
        const openZoom = this.querySelector('.zoom-overlay[style*="display: flex"]');
        if (openZoom) {
          const zoomImage = openZoom.querySelector('.zoom-image');
          this.closeZoom(openZoom, zoomImage);
          return;
        }

        const openModal = this.querySelector('.info-modal[style*="display: block"]');
        if (openModal) {
          this.closeModal(openModal.id);
        }
      }
    });

    console.log('✓ Info modals set up complete');
  }

  openModal(modalId) {
    const modal = this.querySelector(`#${modalId}`);
    if (modal) {
      modal.style.display = 'block';
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
      console.log('✓ Opened modal:', modalId);
    }
  }

  closeModal(modalId) {
    const modal = this.querySelector(`#${modalId}`);
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = ''; // Restore scrolling
      console.log('✓ Closed modal:', modalId);
    }
  }

  closeZoom(zoomOverlay, zoomImage) {
    // Set flag to prevent modal from closing
    this.zoomJustClosed = true;

    zoomOverlay.style.display = 'none';
    zoomImage.src = '';

    // Clear flag after a short delay
    setTimeout(() => {
      this.zoomJustClosed = false;
    }, 100);
  }
}

// Register the custom element
customElements.define('frame-builder', FrameBuilder);
